import type { ReceiptDocument } from '@/lib/printing/types'
import { lira, methodLabel, money, receiptDateTime } from '@/lib/printing/format'

/**
 * Cheap thermal printers speak a single-byte codepage picked by their firmware (commonly
 * CP437), not UTF-8. Sent anything outside 7-bit ASCII, a printer decodes each UTF-8 byte
 * individually against that codepage and prints whatever unrelated glyph sits at that byte
 * value — a wall of symbols that reads as the printer being faulty.
 *
 * Only the last resort now. A line holding anything outside ASCII is drawn as a bitmap instead
 * (see `EscPosBuilder.raster`), so this is reached only where there is no canvas to draw on.
 */
function sanitizeForCodepage(value: string): string {
  return [...value].map((char) => ((char.codePointAt(0) ?? 0) <= 0x7f ? char : '?')).join('')
}

/** True when every character is something the printer's own font can show. */
function isPlainAscii(value: string): boolean {
  for (const char of value) {
    if ((char.codePointAt(0) ?? 0) > 0x7f) return false
  }
  return true
}

/**
 * Whether a line reads right to left, judged by its first letter the way `dir="auto"` does.
 *
 * It decides how the embedded pieces are ordered, not where the line sits: "أرز 5kg" has to keep
 * the 5kg after the rice as an Arabic reader reads it, even on a line pushed to the left edge.
 */
function readsRightToLeft(value: string): boolean {
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0
    // Hebrew, Arabic, Syriac, Thaana and the Arabic presentation forms.
    if ((code >= 0x0590 && code <= 0x08ff) || (code >= 0xfb1d && code <= 0xfdff) || (code >= 0xfe70 && code <= 0xfeff)) {
      return true
    }
    // The first Latin letter settles it the other way.
    if ((code >= 0x41 && code <= 0x5a) || (code >= 0x61 && code <= 0x7a)) return false
  }
  return false
}

/**
 * The typeface a bitmap line is drawn in.
 *
 * Every family here carries Arabic, so whichever the device has, the letters join. Tahoma first
 * because it was drawn for low resolution and survives being thresholded to one bit per dot better
 * than most; the Noto faces are what an Android till actually has.
 */
const RASTER_FONT = '"Tahoma", "Arial", "Noto Sans Arabic", "Noto Naskh Arabic", "Segoe UI", sans-serif'

/**
 * Builds the ESC/POS byte stream a thermal printer understands.
 *
 * ESC/POS is a 1980s control-code protocol, not a page description language: text flows in a
 * fixed-width column and layout is done by padding with spaces. That is why every line here is
 * built to an exact character width rather than laid out.
 */
export class EscPosBuilder {
  private readonly parts: number[] = []
  private readonly encoder = new TextEncoder()

  /** Characters per line at normal size: 48 on 80mm paper, 32 on 58mm. */
  private readonly width: number

  /**
   * Printable dots across the paper at the standard 203 dpi: 576 on 80mm, 384 on 58mm.
   *
   * What a bitmap line is laid out against. The character width above means nothing to an image.
   */
  private readonly dots: number

  /** Tracked so a bitmap line can be positioned the way text would be. See `raster`. */
  private alignment: 'left' | 'center' | 'right' = 'left'
  private isBold = false

  /**
   * How many normal characters each printed character currently takes up.
   *
   * Load-bearing for layout, not decoration. Double-size text is twice as wide, so padding a
   * line to the paper full character count while double size is on produces a line twice the
   * width of the paper — it wraps, and the amount lands alone on a broken second line. That is
   * what made the total, the widest and most important line on the receipt, look like a fault.
   * Every width calculation divides by this.
   */
  private scale = 1

  // A plain field rather than a constructor parameter property: the project builds with
  // erasableSyntaxOnly, which forbids TypeScript syntax that emits code.
  constructor(width: number, dots: number) {
    this.width = width
    this.dots = dots
  }

  static forPaper(widthMm: number): EscPosBuilder {
    return widthMm >= 80 ? new EscPosBuilder(48, 576) : new EscPosBuilder(32, 384)
  }

  private raw(...bytes: number[]): this {
    this.parts.push(...bytes)
    return this
  }

  /** ESC @ — reset to a known state; the printer may hold settings from the last job. */
  init(): this {
    // ESC @ resets the printer, so everything tracked about its state resets with it.
    this.scale = 1
    this.alignment = 'left'
    this.isBold = false
    return this.raw(0x1b, 0x40)
  }

  align(where: 'left' | 'center' | 'right'): this {
    this.alignment = where
    return this.raw(0x1b, 0x61, where === 'left' ? 0 : where === 'center' ? 1 : 2)
  }

  bold(on: boolean): this {
    this.isBold = on
    return this.raw(0x1b, 0x45, on ? 1 : 0)
  }

  /** GS ! — 0x11 doubles both width and height. */
  doubleSize(on: boolean): this {
    this.scale = on ? 2 : 1
    return this.raw(0x1d, 0x21, on ? 0x11 : 0x00)
  }

  /** Characters that fit across the paper right now, allowing for double-size text. */
  private get room(): number {
    return Math.max(1, Math.floor(this.width / this.scale))
  }

  text(value: string): this {
    this.parts.push(...this.encoder.encode(sanitizeForCodepage(value)))
    return this
  }

  line(value = ''): this {
    // Anything the printer's own font cannot show is drawn instead of sent as characters.
    if (!isPlainAscii(value) && typeof document !== 'undefined') {
      return this.raster(value)
    }

    return this.text(value).raw(0x0a)
  }

  /**
   * Draws a line of text and sends it to the printer as a picture.
   *
   * An Arabic product name used to print as a row of question marks. A thermal printer's font is a
   * Latin codepage burnt into its firmware, and even the few that ship an Arabic codepage cannot
   * print Arabic from it: the letters change shape depending on their neighbours and run right to
   * left, and the printer does neither — it would put out isolated letters in the wrong order,
   * which is worse than question marks because it looks like it worked. The browser already does
   * both properly, so it draws the line and the printer just prints the dots. This works on any
   * ESC/POS printer, whatever its codepage.
   * A left-aligned line is cropped to the width of its ink. A product name is most of what gets
   * drawn, and over Bluetooth every byte goes in a 20-byte packet — a full-width strip is several
   * hundred packets per name, a cropped one a few dozen. A centred or right-aligned line keeps the
   * full width with its position baked into the picture, because not every printer honours
   * justification for images, and the store name at the top is not the line to find that out on.
   */
  private raster(value: string): this {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d', { willReadFrequently: true })

    if (!context) {
      return this.text(value).raw(0x0a)
    }

    // A normal ESC/POS character is 24 dots tall, so a drawn line sits in the same space a printed
    // one would and the receipt keeps its rhythm.
    const fontPx = 20 * this.scale
    const rowPx = 26 * this.scale
    const font = `${this.isBold ? 'bold ' : ''}${fontPx}px ${RASTER_FONT}`
    const rtl = readsRightToLeft(value)

    context.font = font
    const rows = wrapToWidth(context, value, this.dots)
    const widest = Math.max(...rows.map((row) => Math.ceil(context.measureText(row).width)), 1)

    // Whole bytes across: each byte is eight dots.
    const width =
      this.alignment === 'left' ? Math.min(this.dots, Math.ceil(widest / 8) * 8) : this.dots
    const height = rows.length * rowPx

    canvas.width = width
    canvas.height = height

    // Resizing a canvas resets its drawing state, so the font is set again.
    context.fillStyle = '#fff'
    context.fillRect(0, 0, width, height)
    context.fillStyle = '#000'
    context.font = font
    context.direction = rtl ? 'rtl' : 'ltr'
    context.textBaseline = 'middle'
    // Absolute, not 'start': 'start' flips with the direction, and a right-to-left name is still
    // meant to sit hard against the left edge like every other line on the slip.
    context.textAlign = 'left'

    rows.forEach((row, index) => {
      const measured = Math.ceil(context.measureText(row).width)
      const x =
        this.alignment === 'center'
          ? Math.max(0, Math.floor((width - measured) / 2))
          : this.alignment === 'right'
            ? Math.max(0, width - measured)
            : 0

      context.fillText(row, x, index * rowPx + rowPx / 2)
    })

    return this.bitmap(context.getImageData(0, 0, width, height))
  }

  /**
   * GS v 0 — print a one-bit raster image.
   *
   * Thresholded rather than dithered. Text is black on white, and dithering the anti-aliased edges
   * of a letter only fringes it with speckle; a straight cut keeps strokes solid. The cut sits a
   * little above the midpoint so the thin joins between Arabic letters survive rather than breaking
   * the word into pieces.
   */
  private bitmap(image: ImageData): this {
    const bytesPerRow = image.width / 8
    const rows = image.height

    this.raw(0x1d, 0x76, 0x30, 0x00, bytesPerRow & 0xff, bytesPerRow >> 8, rows & 0xff, rows >> 8)

    for (let y = 0; y < rows; y++) {
      for (let byte = 0; byte < bytesPerRow; byte++) {
        let bits = 0

        for (let bit = 0; bit < 8; bit++) {
          const offset = (y * image.width + byte * 8 + bit) * 4
          // Red, green and blue are equal for black-on-white, so one channel is the luminance.
          if ((image.data[offset] ?? 255) < 170) {
            bits |= 0x80 >> bit
          }
        }

        this.parts.push(bits)
      }
    }

    return this
  }

  rule(character = '-'): this {
    return this.line(character.repeat(this.room))
  }

  /** A label on the left and an amount hard against the right edge. */
  columns(left: string, right: string): this {
    // A drawn label cannot share a line with printed characters — the printer lays the two out
    // separately — so the label goes on its own line and the amount under it, still hard right.
    if (!isPlainAscii(left) && typeof document !== 'undefined') {
      this.raster(left)
      return this.line(right.padStart(this.room))
    }

    // Measured against what actually fits now, so a double-size total is padded to half the
    // character count and still comes out one line wide.
    const space = Math.max(0, this.room - right.length)
    // Trimmed with ASCII dots. An ellipsis character is outside the printer codepage and would
    // itself be replaced with a question mark, so it would only add noise to a clipped label.
    const clipped = left.length > space ? left.slice(0, Math.max(0, space - 2)) + '..' : left
    return this.line(clipped.padEnd(space) + right)
  }

  feed(lines = 1): this {
    return this.raw(...Array(lines).fill(0x0a))
  }

  /** GS V 66 — partial cut, leaving a tab so the receipt tears cleanly. */
  cut(): this {
    return this.feed(3).raw(0x1d, 0x56, 66, 0)
  }

  /** ESC p — pulse pin 0. The two timings are the standard on/off durations. */
  kickDrawer(): this {
    return this.raw(0x1b, 0x70, 0x00, 25, 250)
  }

  build(): Uint8Array<ArrayBuffer> {
    return toBytes(this.parts)
  }
}

/**
 * Breaks a line into rows that each fit across the paper, at the spaces between words.
 *
 * A thermal printer wraps text by itself, but a picture wider than the paper is simply cut off —
 * so a long product name would lose its end, which is usually the part that tells two sizes of the
 * same thing apart. A single word too long for the paper is left whole on its own row rather than
 * split mid-word, where it would be cut off at the edge either way.
 */
function wrapToWidth(context: CanvasRenderingContext2D, value: string, width: number): string[] {
  const words = value.split(/\s+/).filter((word) => word.length > 0)
  const rows: string[] = []
  let current = ''

  for (const word of words) {
    const candidate = current === '' ? word : `${current} ${word}`

    if (current !== '' && context.measureText(candidate).width > width) {
      rows.push(current)
      current = word
    } else {
      current = candidate
    }
  }

  if (current !== '') rows.push(current)
  return rows.length > 0 ? rows : [value]
}

/**
 * Packs bytes into a Uint8Array explicitly backed by an ArrayBuffer.
 *
 * TypeScript 5.7 made Uint8Array generic over its buffer, and the WebUSB and Web Bluetooth
 * signatures accept only the ArrayBuffer-backed form — a plain `new Uint8Array([...])` widens
 * to ArrayBufferLike and will not assign.
 */
export function toBytes(values: readonly number[]): Uint8Array<ArrayBuffer> {
  const view = new Uint8Array(new ArrayBuffer(values.length))
  view.set(values)
  return view
}

/** Renders a receipt to ESC/POS bytes. */
export function renderEscPos(receipt: ReceiptDocument): Uint8Array<ArrayBuffer> {
  const builder = EscPosBuilder.forPaper(receipt.widthMm)

  builder.init().align('center').bold(true)

  // Double size only while the name actually fits at double size. A long trading name printed
  // twice as wide wraps mid-word across two lines, which looks like a broken printer at the
  // very top of the slip; dropping to normal size keeps it on one line and legible.
  const fitsDouble = receipt.storeName.length <= (receipt.widthMm >= 80 ? 24 : 16)
  builder.doubleSize(fitsDouble)
  builder.line(receipt.storeName)
  builder.doubleSize(false)

  // The branch always prints, on its own line beneath the store and at normal size. Printing
  // it only when it differs from the store name would leave a single-branch shop's receipt
  // showing one name — and that name could then be read as the shop's whole identity, which
  // is exactly what a branch must never look like.
  if (receipt.branchName) {
    builder.line(receipt.branchName)
  }
  builder.bold(false)

  if (receipt.header && receipt.header !== receipt.storeName) builder.line(receipt.header)
  if (receipt.vatNumber) builder.line(`VAT ${receipt.vatNumber}`)

  builder.align('left').rule()
  builder.columns(receipt.number, receiptDateTime(receipt.completedAtUtc))
  builder.line(`Cashier: ${receipt.cashierName}`)
  if (receipt.customerName) builder.line(`Customer: ${receipt.customerName}`)
  if (receipt.reprintCount > 0) {
    builder.align('center').bold(true)
    builder.line(`*** REPRINT #${receipt.reprintCount} ***`)
    builder.bold(false).align('left')
  }
  builder.rule()

  for (const line of receipt.lines) {
    builder.line(line.name)
    // A weighed line prints its unit on both figures — "2500 g x 4.00/kg" — because the
    // quantity and the price are not in the same unit and would otherwise look like an error.
    const amount = Number.isInteger(line.quantity) ? line.quantity : line.quantity.toFixed(3)
    const qty = line.unit && line.priceUnit ? `${amount} ${line.unit}` : amount
    const rate = line.priceUnit
      ? `$${money(line.unitPriceUsd)}/${line.priceUnit}`
      : `$${money(line.unitPriceUsd)}`
    builder.columns(`  ${qty} x ${rate}`, money(line.lineTotalUsd))
    if (line.discountUsd > 0) builder.columns('  Discount', `-${money(line.discountUsd)}`)
  }

  builder.rule()
  builder.columns('Subtotal', money(receipt.subtotalUsd))
  if (receipt.discountUsd > 0) builder.columns('Discount', `-${money(receipt.discountUsd)}`)

  for (const band of receipt.vatBands) {
    if (band.vatUsd !== 0) {
      builder.columns(`VAT ${(band.rate * 100).toFixed(0)}%`, money(band.vatUsd))
    }
  }

  builder.bold(true).doubleSize(true)
  builder.columns('TOTAL', `$${money(receipt.totalUsd)}`)
  builder.doubleSize(false)
  builder.columns('', `LL ${lira(receipt.totalLbp)}`)
  builder.bold(false)
  builder.line(`Rate: 1 USD = ${lira(receipt.lbpRateUsed)} LL`)

  builder.rule()
  for (const payment of receipt.payments) {
    const shown = payment.method === 'CashLbp' ? `LL ${lira(payment.amountLbp)}` : money(payment.amountUsd)
    builder.columns(methodLabel(payment.method), shown)
  }
  if (receipt.changeUsd > 0) builder.columns('Change', money(receipt.changeUsd))
  if (receipt.changeLbp > 0) builder.columns('Change', `LL ${lira(receipt.changeLbp)}`)

  // A credit slip: what went on the account and what was owed once it did. Bold because this is
  // the line the customer is being asked to accept.
  if (receipt.accountChargedUsd > 0) {
    builder.rule().bold(true)
    builder.columns('ON ACCOUNT', `$${money(receipt.accountChargedUsd)}`)
    builder.bold(false)
    if (receipt.accountBalanceAfterUsd !== null) {
      builder.columns('Balance owing', `$${money(receipt.accountBalanceAfterUsd)}`)
    }
    builder.line('Not paid - charged to account.')
  }

  if (receipt.pricesIncludeVat) {
    builder.rule().align('center').line('Prices include VAT')
  }

  if (receipt.footer) builder.align('center').line().line(receipt.footer)

  return builder.align('center').feed(1).cut().build()
}
